import React, { useEffect, useRef, useState } from "react";

import {
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-enterprise";

import DeleteIcon from "@mui/icons-material/Delete";

import SideNav from "component/layouts/sideNav";
import { APIRBAC } from "../../../apihandler/rbac";

import {
  setAlertTitleText,
  setAlertConfirmDeletion,
} from "component/modal/alertModal";

import {
  ADD,
  USER_MANAGEMENT,
  ROLE_POLICIES,
} from "resources/constant/strings/string";

import {
  PRUSSIAN_BLUE,
  SPANISH_GREEN,
  WHITE,
} from "resources/constant/colors/theme";

import { PATH_ROLE_POLICIES } from "resources/constant/url/url";

export default function RolePolicies() {
  const gridRef = useRef();

  const [policies, setPolicies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [openModal, setOpenModal] = useState(false);

  const [formData, setFormData] = useState({
    roleId: "",
    path: "",
    method: "POST",
    companyId: "*",
  });

  const fetchPolicies = async () => {
    try {
      const [policyRes, roleRes] = await Promise.all([
        APIRBAC.Policies().then((res) => res.json()),
        APIRBAC.Roles().then((res) => res.json()),
      ]);

      const rolesData = Array.isArray(roleRes?.response) ? roleRes.response : [];
      const policiesData = Array.isArray(policyRes?.response?.policies)
        ? policyRes.response.policies
        : [];

      setRoles(rolesData);

      const formattedPolicies = policiesData.map((p, index) => {
        const matchedRole = rolesData.find(
          (role) => String(role?.ID ?? role?.id) === String(p.roleId)
        );

        return {
          id: index + 1,
          ...p,
          roleName: matchedRole
            ? matchedRole.RoleName ?? matchedRole.roleName ?? matchedRole.role
            : "Unknown Role",
        };
      });

      setPolicies(formattedPolicies);
    } catch (err) {
      console.error("Error fetching policies/roles", err);
      setPolicies([]);
      setRoles([]);
    }
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  const handleAdd = () => {
    APIRBAC.AddPolicy(formData)
      .then((res) => res.json())
      .then((res) => {
        if (res.code === 200 || res.status === true || res.statusCode === 200) {
          setAlertTitleText("Success", "Policy added successfully");
          setOpenModal(false);
          setFormData({
            roleId: "",
            path: "",
            method: "POST",
            companyId: "*",
          });
          fetchPolicies();
        } else {
          setAlertTitleText("Oops...", res.message || "Failed to add policy");
        }
      })
      .catch((err) => {
        console.error("Error adding policy", err);
        setAlertTitleText("Oops...", "Error adding policy");
      });
  };

  const handleDelete = (rowData) => {
    setAlertConfirmDeletion(
      "Confirmation needed",
      "Do you confirm on proceeding to delete this policy?"
    ).then((consent) => {
      if (!consent.isConfirmed) return;

      APIRBAC.DeletePolicy({
        roleId: rowData.roleId,
        path: rowData.path,
        method: rowData.method,
        companyId: rowData.companyId,
      })
        .then((res) => res.json())
        .then((res) => {
          if (res.code === 200 || res.status === true || res.statusCode === 200) {
            setAlertTitleText("Success", "Policy deleted successfully");
            fetchPolicies();
          } else {
            setAlertTitleText(
              "Oops...",
              res.message || "Failed to delete policy"
            );
          }
        })
        .catch((err) => {
          console.error("Error deleting policy", err);
          setAlertTitleText("Oops...", "Error deleting policy");
        });
    });
  };

  const columnDefs = [
    {
      wrapText: true,
      autoHeight: true,
      headerName: "Role ID",
      field: "roleId",
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      wrapText: true,
      autoHeight: true,
      headerName: "Role Name",
      field: "roleName",
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      wrapText: true,
      autoHeight: true,
      headerName: "Route Path",
      field: "path",
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      wrapText: true,
      autoHeight: true,
      headerName: "HTTP Action",
      field: "method",
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      wrapText: true,
      autoHeight: true,
      headerName: "Domain (Company ID)",
      field: "companyId",
      cellStyle: {
        "border-right-color": "#e2e2e2",
      },
    },
    {
      flex: 0.6,
      headerName: "Actions",
      field: "id",
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellRenderer: (params) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <IconButton
            aria-label="delete"
            onClick={() => handleDelete(params.data)}
          >
            <DeleteIcon style={{ color: PRUSSIAN_BLUE, width: "20px" }} />
          </IconButton>
        </div>
      ),
    },
  ];

  const defaultColDef = {
    resizable: true,
    sortable: true,
    editable: false,
    flex: 1,
    filter: "agTextColumnFilter",
    filterParams: {
      caseSensitive: false,
      defaultOption: "startsWith",
    },
    floatingFilter: true,
  };

  return (
    <SideNav
      currentComponent={USER_MANAGEMENT}
      subComponent={ROLE_POLICIES}
      subComponentPath={PATH_ROLE_POLICIES}
      detailComponent=""
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          marginBottom: 10,
          marginRight: 10,
        }}
      >
        <Button
          style={{
            backgroundColor: SPANISH_GREEN,
            textTransform: "capitalize",
            fontSize: 14,
            color: WHITE,
            minWidth: "180px",
            minHeight: "45px",
          }}
          onClick={() => setOpenModal(true)}
        >
          {ADD}
        </Button>
      </div>

      <div className="App">
        <div className="ag-theme-alpine" style={{ height: "570px" }}>
          <AgGridReact
            ref={gridRef}
            rowData={policies}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            paginationAutoPageSize={true}
            pagination={true}
            suppressBrowserResizeObserver={true}
          />
        </div>
      </div>

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add New RBAC Access Route</DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="Role"
            fullWidth
            value={formData.roleId}
            onChange={(e) =>
              setFormData({ ...formData, roleId: e.target.value })
            }
          >
            {roles
              .filter(
                (role) =>
                  role && (role.ID !== undefined || role.id !== undefined)
              )
              .map((role) => {
                const roleValue = role.ID ?? role.id;
                const roleLabel =
                  role.RoleName ??
                  role.roleName ??
                  role.role ??
                  "Unknown Role";

                return (
                  <MenuItem key={String(roleValue)} value={String(roleValue)}>
                    {roleLabel}
                  </MenuItem>
                );
              })}
          </TextField>

          <TextField
            margin="dense"
            label="Backend Route Path (e.g., /api/v1/indent)"
            fullWidth
            value={formData.path}
            onChange={(e) =>
              setFormData({ ...formData, path: e.target.value })
            }
          />

          <TextField
            select
            margin="dense"
            label="HTTP Action"
            fullWidth
            value={formData.method}
            onChange={(e) =>
              setFormData({ ...formData, method: e.target.value })
            }
          >
            <MenuItem value="POST">POST (Create)</MenuItem>
            <MenuItem value="PUT">PUT (Update)</MenuItem>
            <MenuItem value="DELETE">DELETE (Remove)</MenuItem>
            <MenuItem value="*">* (All Permissions)</MenuItem>
          </TextField>

          <TextField
            margin="dense"
            label="Company ID"
            fullWidth
            value={formData.companyId}
            onChange={(e) =>
              setFormData({ ...formData, companyId: e.target.value })
            }
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained" color="primary">
            Save Policy
          </Button>
        </DialogActions>
      </Dialog>
    </SideNav>
  );
}